import React from 'react';
import Box from '@cloudscape-design/components/box';
import { colorForStore } from './storeColors';
import { EventTypeIndicator } from '../../components/EventTypeIndicator';
import { AiBadge } from '../../components/AiBadge';
import { CausedByChip } from '../../components/CausedByChip';
import { resolveEventType } from '../../utils/resolveEventType';
import { isBedrockEvent } from '../../utils/isBedrockEvent';
import { formatTimestamp, getStoredTimeFormat } from '../../utils/timeFormat';

export interface TimelineEvent {
  metadata?: {
    eventId?: string;
    eventType?: string;
    timestamp?: string;
    causedBy?: string | string[] | null;
    source?: string;
    [k: string]: unknown;
  };
  data?: Record<string, unknown> | null;
  _sourceStore?: string;
  pk?: string;
  [k: string]: unknown;
}

interface Props {
  events: TimelineEvent[];
  selectedEventId?: string;
  onSelect: (evt: TimelineEvent) => void;
}

/**
 * Vertical chronological list of cross-store events. Each row shows a
 * store-colored badge, the resolved event type, an AI badge for Bedrock
 * output events, the relative timestamp, and a causedBy chip.
 *
 * Selection is controlled by the parent (URL-state driven) so that clicking
 * a row updates the URL and the detail panel simultaneously.
 */
export function StreamTimeline({ events, selectedEventId, onSelect }: Props) {
  const timeFormat = getStoredTimeFormat();
  if (events.length === 0) {
    return <Box color="text-body-secondary" padding="l">No events for this stream.</Box>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
      {events.map((evt, idx) => {
        const md = evt.metadata || {};
        const store = evt._sourceStore || '';
        const eventType = resolveEventType(evt);
        const eventId = md.eventId || `t-${idx}`;
        const isSelected = selectedEventId === md.eventId;
        return (
          <div
            key={eventId}
            onClick={() => onSelect(evt)}
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 24px 1fr',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 6,
              background: isSelected ? '#eef4ff' : 'transparent',
              borderLeft: `4px solid ${colorForStore(store)}`,
            }}
            onMouseEnter={(e) => {
              if (!isSelected) e.currentTarget.style.background = '#f4f6f9';
            }}
            onMouseLeave={(e) => {
              if (!isSelected) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ fontSize: 12, color: '#687078', fontFamily: 'monospace' }}>
              {formatTimestamp(md.timestamp, timeFormat)}
            </span>
            <span
              aria-label={`Store: ${store}`}
              title={store}
              style={{
                width: 12, height: 12, borderRadius: 6,
                background: colorForStore(store),
                display: 'inline-block',
              }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>{store}</span>
              <span style={{ color: '#9ba7b3' }}>·</span>
              <EventTypeIndicator eventType={eventType} />
              {isBedrockEvent(evt) && <AiBadge />}
              <span style={{ color: '#9ba7b3' }}>·</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                <CausedByChip
                  causedBy={md.causedBy as string | string[] | null | undefined}
                  storeName={store}
                />
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
